<template>
  <div
    class="cursor-pointer transition-opacity duration-500 ease-in-out"
    :class="{ 'opacity-0': !isActive, 'opacity-100': isActive }"
    @click="handleClick"
  >
    <div class="px-0 lg:px-4">
      <div
        class="relative bg-neutral-200 dark:bg-[#222222] overflow-hidden flex flex-col lg:flex-row py-4 md:p-4 lg:p-9 gap-4 lg:gap-11 rounded-none lg:rounded-3xl w-full"
      >
        <!-- Content Area -->
        <div
          class="flex-1 lg:flex-[35] flex flex-col justify-center items-center text-center order-2 lg:order-1 py-4 lg:py-0 px-4 lg:px-0"
        >
          <!-- Category Badge -->
          <div v-if="article.category" class="mb-4">
            <span
              class="inline-block px-3 py-1 text-neutral-50 dark:text-white bg-neutral-500 dark:bg-neutral-700 rounded font-sans font-bold text-base lg:text-2xl leading-none tracking-tighter text-center"
            >
              {{ article.category }}
            </span>
          </div>

          <!-- Main Title -->
          <div class="mb-0 lg:mb-4">
            <h2
              class="text-neutral-950 dark:text-white mb-7 line-clamp-4 lg:line-clamp-none font-sans font-extrabold text-2xl lg:text-5xl leading-tight tracking-tighter text-center pb-0.5"
            >
              {{ article.title }}
            </h2>
          </div>

          <!-- Author -->
          <div>
            <p
              class="dark:text-gray-400 font-sans font-medium text-base lg:text-2xl leading-none tracking-tighter text-center"
            >
              <span v-if="article.authors?.[0]">By {{ article.authors[0].name }}</span>
            </p>
          </div>
        </div>

        <!-- Image Area -->
        <div class="w-full lg:flex-[65] relative order-1 lg:order-2 lg:mx-0">
          <div class="w-full lg:aspect-[16/9] h-full min-w-full">
            <img
              v-if="article.image?.src"
              :src="article.image.src"
              :alt="article.image.alt || article.title"
              class="w-screen lg:w-full h-full object-cover rounded-none lg:rounded-3xl"
            />
            <div
              v-else
              class="w-screen lg:w-full h-full bg-gray-200 flex items-center justify-center rounded-none lg:rounded-3xl"
            >
              <span class="text-gray-500">No image available</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { CarouselArticle } from '~/types/carousel-article';

interface Props {
  article: CarouselArticle;
  index: number;
  isActive: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  activate: [index: number];
}>();

const handleClick = () => {
  if (props.isActive) {
    // If this is the active/center slide, navigate to the article
    navigateTo(props.article.path || props.article._path);
  } else {
    // If this is not the active slide, make it active
    emit('activate', props.index);
  }
};
</script>
